import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "@/lib/utils";
import { motion } from "framer-motion";
import { Package, MapPin, ArrowRight, CheckCircle2, Smartphone, Clock, Info, CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import MapLocationPicker from "@/components/MapLocationPicker";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";



const CourierBookingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState("new");
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_phone: user.phone || prev.customer_phone,
        customer_email: user.email || prev.customer_email,
      }));
    }
  }, [user]);

  const [formData, setFormData] = useState({
    pickup_address: "",
    dropoff_address: "",
    package_size: "",
    urgency: "",
    package_description: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    notes: ""
  });

  const packageSizes = [
    { value: "envelope", label: "Envelope / Documents", desc: "Letters, contracts, small documents" },
    { value: "small", label: "Small Package", desc: "Up to 5 lbs, fits in a shoebox" },
    { value: "medium", label: "Medium Package", desc: "5-20 lbs, fits in a backpack" },
    { value: "large", label: "Large Package", desc: "20-50 lbs, requires two hands" }
  ];

  const urgencyOptions = [
    { value: "standard", label: "Standard (2-4 hours)", multiplier: "1x" },
    { value: "express", label: "Express (1-2 hours)", multiplier: "1.5x" },
    { value: "same_day", label: "Priority (Under 1 hour)", multiplier: "2x" }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getEstimate = async () => {
    if (!formData.pickup_address || !formData.dropoff_address || !formData.package_size) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!pickupCoords) {
      toast.error("Please select a valid pickup location on the map");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude };
      const res = await axios.post(`${API}/courier/estimate`, payload);
      setEstimate(res.data);
      setStep(2);
    } catch {
      toast.error("Failed to get estimate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitBooking = async () => {
    if (!formData.customer_name || !formData.customer_phone || !formData.customer_email || !formData.package_description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/courier/booking`, { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude });
      setStep(3);
      toast.success("Booking submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const proceedToPayment = async () => {
    if (!formData.package_description) {
      toast.error("Please add a package description");
      return;
    }

    setPaymentLoading(true);
    try {
      const bookingResponse = await axios.post(`${API}/courier/booking`, { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude });
      const booking = bookingResponse.data;
      const serviceData = {
        booking_id: booking.id,
        booking_type: "courier",
        amount: estimate.max_price,
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        service_type: "courier",
        pickup_address: formData.pickup_address,
        dropoff_address: formData.dropoff_address,
        pickup_coords: pickupCoords,
        dropoff_coords: dropoffCoords,
      };

      if (selectedCardId && selectedCardId !== "new") {
        const authResponse = await axios.post(`${API}/payments/authorize-hold`, {
          ...serviceData,
          payment_method_id: selectedCardId,
        }, { withCredentials: true });
        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your driver...");
          setStep(3);
        } else {
          toast.error("Payment authorization failed. Please try again.");
        }
        setPaymentLoading(false);
      } else {
        const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
          ...serviceData,
          origin_url: window.location.origin,
        });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      console.error("Authorization error:", error);
      toast.error(getErrorMessage(error, "Payment authorization failed. Please try another payment method."));
      setPaymentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="courier-booking-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_stackcart-app/artifacts/xwwv0r3q_close-up-delivery-person-with-parcels_23-2149095883.jpg')" }}
        ></div>
        {/* Dark Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-sky-900/90 via-sky-800/85 to-sky-900/80"></div>
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <Package className="w-8 h-8 text-sky-500" />
            </div>
            <h1 className="heading-2 text-white mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>Courier Delivery</h1>
            <p className="text-lg md:text-xl leading-relaxed text-white" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
              Fast package delivery with drivers using sedans and SUVs. From documents to parcels, we have got you covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-container py-12">
        <div className="max-w-3xl mx-auto">
          {/* Package Details */}
          {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8" data-testid="courier-step-1">
              <h2 className="heading-3 text-emerald-950 mb-6">Package Details</h2>

              <div className="space-y-6">
                {/* Addresses with Map */}
                <MapLocationPicker
                  pickupAddress={formData.pickup_address}
                  dropoffAddress={formData.dropoff_address}
                  onPickupChange={(val) => handleInputChange("pickup_address", val)}
                  onDropoffChange={(val) => handleInputChange("dropoff_address", val)}
                  onPickupCoordsChange={setPickupCoords}
                  onDropoffCoordsChange={setDropoffCoords}
                  pickupPlaceholder="123 Main St, Toronto"
                  dropoffPlaceholder="456 Oak Ave, Mississauga"
                  serviceType="courier"
                />

                {/* Package Size */}
                <div>
                  <Label className="text-emerald-950 font-medium mb-3 block">Package Size *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {packageSizes.map((size) => (
                      <button
                        key={size.value}
                        type="button"
                        onClick={() => handleInputChange("package_size", size.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${formData.package_size === size.value ? "border-sky-500 bg-sky-50" : "border-stone-200 hover:border-sky-300"}`}
                        data-testid={`package-size-${size.value}`}
                      >
                        <div className="font-medium text-emerald-950">{size.label}</div>
                        <div className="text-sm text-stone-500">{size.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-emerald-950 font-medium mb-2 block">Package Description *</Label>
                  <Textarea
                    className="input-style min-h-[80px]"
                    placeholder="Describe your package (e.g., documents, electronics, fragile items...)"
                    value={formData.package_description}
                    onChange={(e) => handleInputChange("package_description", e.target.value)}
                    data-testid="package-description"
                  />
                </div>

                <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full py-6 font-bold" onClick={getEstimate} disabled={loading} data-testid="get-estimate-btn">
                  {loading ? "Getting Estimate..." : "Continue"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
            )}

            {step === 2 && estimate && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 mt-6" data-testid="payment-step">
                <h2 className="text-xl font-bold text-emerald-950 mb-4">Payment</h2>
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-stone-600">Estimated Price</p>
                  <p className="text-3xl font-bold text-sky-700">${estimate.max_price.toFixed(2)} <span className="text-sm font-normal text-stone-500">CAD</span></p>
                </div>
                <SavedCardsSelector onSelect={setSelectedCardId} selectedCardId={selectedCardId} />
                <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full py-6 font-bold mt-4" onClick={proceedToPayment} disabled={paymentLoading} data-testid="pay-btn">
                  {paymentLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Pay & Find Driver <ArrowRight className="w-5 h-5 ml-2" /></>}
                </Button>
              </motion.div>
            )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CourierBookingPage;
